import { useEffect, useRef, useState } from "react";
import { GameEngine } from "../game/engine/GameEngine";
import type { HudSnapshot, LevelConfig } from "../game/engine/types";
import { InputController } from "../game/input";
import { HUD } from "./HUD";
import { TouchControls } from "./TouchControls";

interface Props {
  levelConfig?: Partial<LevelConfig> & { seed?: number };
  selfName: string;
  opponent?: { name: string; survivedMs: number; alive: boolean } | null;
  onGameOver: (finalScore: number) => void;
  /** Delay before the engine starts ticking, e.g. for a countdown overlay. */
  startDelayMs?: number;
}

/**
 * Bridges React <-> the framework-agnostic GameEngine.
 * Owns the canvas DOM element, an InputController, and the engine instance.
 * Cleans up listeners + cancels RAF on unmount.
 */
export function GameCanvas({
  levelConfig,
  selfName,
  opponent,
  onGameOver,
  startDelayMs = 0,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<GameEngine | null>(null);
  const inputRef = useRef<InputController | null>(null);
  const [hud, setHud] = useState<HudSnapshot | null>(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const input = new InputController();
    inputRef.current = input;
    input.attach(window);

    const engine = new GameEngine({
      canvas,
      input,
      levelConfig,
      events: {
        onHudUpdate: (snap) => {
          setHud(snap);
          setPaused(engine.isPaused());
        },
        onGameOver: (finalScore) => onGameOver(finalScore),
      },
    });
    engineRef.current = engine;

    const startTimer = window.setTimeout(() => {
      engine.start();
    }, Math.max(0, startDelayMs));

    return () => {
      window.clearTimeout(startTimer);
      engine.stop();
      input.detach(window);
      engineRef.current = null;
      inputRef.current = null;
    };
    // Intentionally re-create the engine whenever level seed/config changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelConfig?.seed]);

  return (
    <div className="relative mx-auto flex h-full w-full items-center justify-center">
      <div className="relative w-full max-w-[1280px] aspect-[1280/480]">
        <canvas
          ref={canvasRef}
          className="block h-full w-full rounded-2xl border border-white/15 bg-black shadow-2xl"
        />
        <HUD hud={hud} selfName={selfName} opponent={opponent ?? null} />
        {inputRef.current && <TouchControls input={inputRef.current} />}
        {paused && (
          <button
            type="button"
            onClick={() => engineRef.current?.resume()}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 rounded-2xl bg-cap-deep/70 backdrop-blur-sm"
          >
            <span className="font-display text-5xl tracking-widest text-white">PAUSED</span>
            <span className="text-sm uppercase tracking-widest text-white/60">Tap or press P to resume</span>
          </button>
        )}
      </div>
    </div>
  );
}
